import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import * as _ from 'lodash';
import { FcpBadgeColor, FcpBadgeSize } from '../fcp-badge';
import { FcpIcon } from '../fcp-icon';
import { FcpIconSVGColors, IconNameMap } from '../fcp-icon-svg';

@Component({
    selector: 'fcp-tab',
    templateUrl: './fcp-tab.component.html',
    styleUrls: ['./fcp-tab.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FcpTabComponent implements OnInit {
    @Input() type: TabType = TabType.Contained;
    @Input() size: TabSize = TabSize.Small;
    @Input() style?: TabStyle = TabStyle.FullWidthLine;
    @Input() items: Item[];
    @Input() initialValue?: string;
    @Input() selectedValue?: string;

    @Output() selected = new EventEmitter<string>();

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        const initialItem = _.find(this.items, { value: this.initialValue });

        this.selectedValue =
            initialItem?.value && !initialItem?.disabled
                ? this.initialValue
                : _.find(this.items, (item) => item.disabled !== true).value;
        this.cdr.markForCheck();
    }

    onClickTab(value: string) {
        if (_.find(this.items, { value }).disabled) return;

        this.selectedValue = value;
        this.selected.emit(value);
        this.cdr.markForCheck();
    }

    getIconColor(item: Item): FcpIconSVGColors {
        if (item.disabled) {
            return FcpIconSVGColors.NeutralLight;
        }
        const color =
            this.isIconOnlyTab(item) && item.value === this.selectedValue
                ? FcpIconSVGColors.Neutral
                : FcpIconSVGColors.NeutralDark;
        this.cdr.markForCheck();
        return color;
    }

    isSvgIcon(icon: any): boolean {
        return typeof icon === 'string' && Object.keys(IconNameMap).includes(icon as string);
    }

    isIconOnlyTab(item: Item): boolean {
        return !item.text && item.icon && this.isSvgIcon(item.icon.name);
    }

    getTabClasses(item: Item): any {
        const classes = {
            'fcp-tab-content': true,
            [this.type]: true,
            [this.size]: true,
            [this.style]: true,
            disabled: item.disabled,
            selected: item.value === this.selectedValue,
            'icon-only': this.isIconOnlyTab(item)
        };
        this.cdr.markForCheck();
        return classes;
    }
}

interface Item {
    text?: string; // Optional to support icon-only tabs
    value: string;
    disabled?: boolean;
    icon?: Omit<FcpIcon, 'icon'> & {
        name: FcpIcon['icon'] | IconName;
        position?: 'start' | 'end';
    };
    badge?: {
        value: string;
        color: FcpBadgeColor;
        size: FcpBadgeSize;
    };
}

export enum TabType {
    Contained = 'contained',
    Line = 'line'
}

export enum TabSize {
    Big = 'big',
    Small = 'small'
}

export enum TabStyle {
    FitContentLine = 'fit-content-line',
    FullWidthLine = 'full-width-line'
}
